import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getAccessToken, saveAccessToken, clearSession, refreshSession } from "../../services/session";

export default function useAuth() {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});

  useEffect(() => {
    const expire = () => { clearSession(); setIsAuth(false); setUser({}); };
    const requestId = api.interceptors.request.use(config => {
      const token = getAccessToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    }, error => Promise.reject(error));
    const responseId = api.interceptors.response.use(response => response, async error => {
      const original = error.config;
      const authError = String(error.response?.data?.error || "");
      const requestToken = original?.headers?.Authorization;
      const currentSession = () => !requestToken || requestToken === `Bearer ${getAccessToken()}`;
      if ([401, 403].includes(error.response?.status) && original && !original._retry &&
          !original._skipAuthRefresh && !original.url?.includes("/auth/") &&
          (authError.includes("Invalid token") || authError.includes("ERR_SESSION_EXPIRED"))) {
        original._retry = true;
        try {
          const data = await refreshSession();
          original.headers.Authorization = `Bearer ${data.token}`;
          return api(original);
        } catch (refreshError) {
          if ([401, 403].includes(refreshError.response?.status) && currentSession()) expire();
          return Promise.reject(refreshError);
        }
      }
      if (error.response?.status === 401 && original?.url?.includes("/auth/refresh_token") && currentSession()) expire();
      return Promise.reject(error);
    });
    let active = true;
    (async () => {
      if (getAccessToken()) {
        try {
          const data = await refreshSession();
          if (active) { setIsAuth(true); setUser(data.user); }
        } catch (error) { if (active) { setIsAuth(false); setUser({}); toastError(error); } }
      }
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
      api.interceptors.request.eject(requestId);
      api.interceptors.response.eject(responseId);
    };
  }, []);

  useEffect(() => {
    if (!isAuth || !user.id) return;
    const socket = openSocket();
    socket.on("user", data => {
      if (data.action === "update" && data.user.id === user.id) setUser(data.user);
    });
    return () => socket.disconnect();
  }, [isAuth, user.id]);

  useEffect(() => {
    if (!isAuth) return;
    let stopped = false;
    let timer;
    const renew = async () => {
      clearTimeout(timer);
      const tokenAtStart = getAccessToken();
      try {
        await refreshSession();
      } catch (error) {
        if ([401, 403].includes(error.response?.status)) {
          if (!stopped && getAccessToken() === tokenAtStart) { clearSession(); setIsAuth(false); setUser({}); history.replace("/login"); }
          return;
        }
        // Offline or server unavailable: keep the session and retry.
      }
      if (!stopped) { clearTimeout(timer); timer = setTimeout(renew, 5 * 60 * 1000); }
    };
    const resume = () => { if (document.visibilityState === "visible") renew(); };
    timer = setTimeout(renew, 5 * 60 * 1000);
    window.addEventListener("online", renew);
    document.addEventListener("visibilitychange", resume);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener("online", renew);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [isAuth]);

  const handleLogin = async values => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", values, { _skipAuthRefresh: true });
      saveAccessToken(data.token);
      setUser(data.user);
      setIsAuth(true);
      toast.success(i18n.t("auth.toasts.success"));
      history.push("/tickets");
    } catch (error) {
      if (error.response?.status === 401 && !window.location.pathname.startsWith("/app/")) {
        try {
          const { data: catalog } = await api.get("/saas/catalog");
          if (catalog.enabled) {
            const { data } = await api.post("/saas/session", values, { _skipAuthRefresh: true });
            sessionStorage.setItem("zapticket:billing", data.token);
            if (data.company.workspaceUrl) {
              const base = (api.defaults.baseURL || "").replace(/\/$/, "");
              const login = await api.post(`${base}/workspaces/${data.company.slug}/auth/login`, values, { _skipAuthRefresh: true });
              localStorage.setItem(`token:${data.company.slug}`, JSON.stringify(login.data.token));
              window.location.assign(`/app/${data.company.slug}/tickets`);
            } else window.location.assign("/assinatura");
            return;
          }
        } catch (billingError) { toastError(billingError); return; }
      }
      toastError(error);
    }
    finally { setLoading(false); }
  };
  const handleLogout = async () => {
    setLoading(true);
    try {
      await api.delete("/auth/logout", { _skipAuthRefresh: true });
      clearSession();
      setIsAuth(false);
      setUser({});
      history.push("/login");
    } catch (error) { toastError(error); }
    finally { setLoading(false); }
  };
  return { isAuth, user, loading, handleLogin, handleLogout };
}
